import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { EmpAvatar } from "@/components/EmpAvatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  LayoutDashboard, Users, Clock, CalendarDays, FileText, Network,
  Bell, LogOut, Menu, X, Building2, ChevronRight, Settings,
  Award, Megaphone, FolderOpen, CalendarRange, Moon, Sun, Target, Lightbulb, Package, Receipt, Contact, Percent, Coins,
} from "lucide-react";
import { cn } from "@/lib/utils";

const TZ = "Asia/Singapore";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "employee"] },
  { to: "/attendance", label: "Attendance", icon: Clock, roles: ["admin", "manager", "employee"] },
  { to: "/leaves", label: "Leave", icon: CalendarDays, roles: ["admin", "manager", "employee"] },
  { to: "/payslips", label: "Payslips", icon: FileText, roles: ["admin", "manager", "employee"] },
  { to: "/commissions", label: "Commissions & Bonuses", icon: Percent, roles: ["admin"] },
  { to: "/my-commission", label: "My Commission", icon: Coins, roles: ["admin", "manager", "employee"] },
  { to: "/claims", label: "Claims", icon: Receipt, roles: ["admin", "manager", "employee"] },
  { to: "/employees", label: "Employees", icon: Users, roles: ["admin"] },
  { to: "/org-chart", label: "Org Chart", icon: Network, roles: ["admin"] },
  { to: "/directory", label: "Directory", icon: Contact, roles: ["admin", "manager", "employee"] },
  { to: "/performance", label: "Performance", icon: Target, roles: ["admin", "manager", "employee"] },
  { to: "/recognition", label: "Recognition", icon: Award, roles: ["admin", "manager", "employee"] },
  { to: "/suggestions", label: "Suggestions", icon: Lightbulb, roles: ["admin", "manager", "employee"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["admin", "manager", "employee"] },
  { to: "/documents", label: "Documents", icon: FolderOpen, roles: ["admin", "manager", "employee"] },
  { to: "/inventory", label: "Inventory", icon: Package, roles: ["admin", "manager"] },
  { to: "/calendar", label: "Calendar", icon: CalendarRange, roles: ["admin", "manager", "employee"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

const Clock2 = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="hidden md:flex flex-col items-end leading-tight" data-testid="sgt-clock">
      <span className="font-mono-num text-sm font-semibold text-foreground">
        {now.toLocaleTimeString("en-SG", { timeZone: TZ, hour12: true })}
      </span>
      <span className="overline">SGT · {now.toLocaleDateString("en-SG", { timeZone: TZ, weekday: "short", day: "2-digit", month: "short" })}</span>
    </div>
  );
};

const Notifications = () => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setData(r.data);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const markRead = async () => {
    await api.put("/notifications/read");
    load();
  };
  return (
    <Popover onOpenChange={(o) => o && data.unread && markRead()}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-popover" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-border">
          <h4 className="font-heading font-bold text-sm">Notifications</h4>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.notifications.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>
          )}
          {data.notifications.map((n) => (
            <div key={n.id} className={cn("px-4 py-3 border-b border-border/60 text-sm", !n.read && "bg-accent/40")}>
              <p className="text-foreground">{n.message}</p>
              <p className="overline mt-1">{new Date(n.created_at).toLocaleString("en-SG", { timeZone: TZ, dateStyle: "medium", timeStyle: "short" })}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(() => {
    try { return JSON.parse(localStorage.getItem("branding")) || { company_name: "OKRStaff", logo_file_id: null }; }
    catch { return { company_name: "OKRStaff", logo_file_id: null }; }
  });
  useEffect(() => {
    api.get("/settings").then((r) => { setBrand(r.data); localStorage.setItem("branding", JSON.stringify(r.data)); }).catch(() => {});
    const onUpd = (e) => setBrand(e.detail);
    window.addEventListener("branding-updated", onUpd);
    return () => window.removeEventListener("branding-updated", onUpd);
  }, []);
  const items = NAV.filter((n) => n.roles.includes(user?.role) && !(n.to === "/attendance" && brand.attendance_enabled === false));
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const roleLabel = { admin: "HR Admin", manager: "Manager", employee: "Employee" }[user?.role] || "";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed z-40 inset-y-0 left-0 w-64 bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] flex flex-col transition-transform duration-200 md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
          <div className="h-8 w-8 rounded-md bg-destructive flex items-center justify-center overflow-hidden">
            {brand.logo_file_id ? <img src={fileUrl(brand.logo_file_id)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-5 w-5 text-white" />}
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight truncate">{brand.company_name || "OKRStaff"}</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${it.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors group",
                  isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <it.icon className="h-[18px] w-[18px]" />
                  <span>{it.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto opacity-60" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => { navigate("/profile"); setOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white/5 transition-colors text-left"
            data-testid="profile-link"
          >
            <EmpAvatar emp={{ full_name: user?.name }} size="h-9 w-9" className="border-white/20" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 truncate">{roleLabel}</p>
            </div>
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-background/70 backdrop-blur-xl border-b border-border flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div>
              <p className="overline">Singapore</p>
              <h1 className="font-heading font-bold text-lg leading-none">Human Resources</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock2 />
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle" title="Toggle theme">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Notifications />
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Log out">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full mx-auto">
          {localStorage.getItem("act_tenant") && user?.platform_role === "super_admin" && (
            <div className="mb-4 flex items-center justify-between gap-3 px-4 py-2.5 rounded-md bg-destructive/10 border border-destructive/30 text-sm" data-testid="impersonation-banner">
              <span className="text-destructive font-medium">Viewing tenant: {localStorage.getItem("act_tenant_name") || "—"}</span>
              <Button size="sm" variant="outline" onClick={() => { localStorage.removeItem("act_tenant"); localStorage.removeItem("act_tenant_name"); window.location.href = "/platform"; }} data-testid="exit-impersonation">Exit tenant view</Button>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
};
